(function (window, document) {
    'use strict';

    var finePointerQuery = window.matchMedia('(min-width: 768px) and (hover: hover) and (pointer: fine)');
    var reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    // Calm motion is always present; hovering eases toward the faster wind pace.
    var defaultAmbientMotionSpeed = .7;
    var defaultHoverMotionSpeed = 2;
    var defaultAmbientMotionStrength = 5;
    var clothSelector = '[data-music-library-logo-effect="cloth"]';
    var exitEventName = 'ikson:music-library-logo-cloth-exit';
    var restoreEventName = 'ikson:music-library-logo-cloth-restore';
    var destroyEventName = 'ikson:music-library-logo-cloth-destroy';
    var lifecycleObserver = null;

    function dispatchLifecycleEvent(eventName) {
        var surfaces = document.querySelectorAll(clothSelector + '[data-music-library-logo-cloth-setup="true"]');

        Array.prototype.forEach.call(surfaces, function (surface) {
            if (surface.isConnected) {
                surface.dispatchEvent(new window.Event(eventName));
            }
        });
    }

    function restorePersistedPage(event) {
        var pendingSurfaces;

        if (event.persisted) {
            dispatchLifecycleEvent(restoreEventName);
            return;
        }

        pendingSurfaces = document.querySelectorAll(clothSelector + '[data-music-library-logo-cloth-setup="true"]:not(.is-logo-distortion-ready)');
        Array.prototype.forEach.call(pendingSurfaces, function (surface) {
            surface.dispatchEvent(new window.Event(restoreEventName));
        });
    }

    function effectIsAvailable() {
        return finePointerQuery.matches && !reducedMotionQuery.matches;
    }

    function collectClothSurfaces(root, surfaces) {
        var descendants;

        if (!root || (root.nodeType !== 1 && root.nodeType !== 9 && root.nodeType !== 11)) {
            return;
        }

        if (typeof root.matches === 'function' && root.matches(clothSelector)) {
            surfaces.push(root);
        }

        if (typeof root.querySelectorAll !== 'function') {
            return;
        }

        descendants = root.querySelectorAll(clothSelector);
        Array.prototype.forEach.call(descendants, function (surface) {
            surfaces.push(surface);
        });
    }

    function observeClothLifecycle() {
        if (lifecycleObserver || typeof window.MutationObserver !== 'function' || !document.documentElement) {
            return;
        }

        lifecycleObserver = new window.MutationObserver(function (records) {
            var surfaces = [];

            Array.prototype.forEach.call(records, function (record) {
                Array.prototype.forEach.call(record.addedNodes, function (node) {
                    collectClothSurfaces(node, surfaces);
                });
                Array.prototype.forEach.call(record.removedNodes, function (node) {
                    collectClothSurfaces(node, surfaces);
                });
            });

            surfaces.filter(function (surface, index) {
                return surfaces.indexOf(surface) === index;
            }).forEach(function (surface) {
                if (surface.isConnected) {
                    setupWebGLCloth(surface);
                } else if (surface.getAttribute('data-music-library-logo-cloth-setup') === 'true') {
                    surface.dispatchEvent(new window.Event(destroyEventName));
                }
            });
        });

        lifecycleObserver.observe(document.documentElement, {
            childList: true,
            subtree: true
        });
    }

    function clamp(value, minimum, maximum) {
        return Math.min(maximum, Math.max(minimum, value));
    }

    function getTuningNumber(parameter, fallback, minimum, maximum) {
        var requestedValue;

        if (typeof window.URLSearchParams !== 'function') {
            return fallback;
        }

        requestedValue = parseFloat(new window.URLSearchParams(window.location.search).get(parameter));

        return isFinite(requestedValue)
            ? clamp(requestedValue, minimum, maximum)
            : fallback;
    }

    function createShader(gl, type, source) {
        var shader = gl.createShader(type);

        gl.shaderSource(shader, source);
        gl.compileShader(shader);

        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
            gl.deleteShader(shader);
            return null;
        }

        return shader;
    }

    function createProgram(gl, vertexSource, fragmentSource) {
        var vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexSource);
        var fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
        var program;

        if (!vertexShader || !fragmentShader) {
            return null;
        }

        program = gl.createProgram();
        gl.attachShader(program, vertexShader);
        gl.attachShader(program, fragmentShader);
        gl.linkProgram(program);
        gl.deleteShader(vertexShader);
        gl.deleteShader(fragmentShader);

        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
            gl.deleteProgram(program);
            return null;
        }

        return program;
    }

    function createMesh(columns, rows) {
        var textureCoordinates = [];
        var indices = [];
        var x;
        var y;

        for (y = 0; y <= rows; y += 1) {
            for (x = 0; x <= columns; x += 1) {
                textureCoordinates.push(x / columns, y / rows);
            }
        }

        for (y = 0; y < rows; y += 1) {
            for (x = 0; x < columns; x += 1) {
                var topLeft = (y * (columns + 1)) + x;
                var topRight = topLeft + 1;
                var bottomLeft = topLeft + columns + 1;
                var bottomRight = bottomLeft + 1;

                indices.push(topLeft, bottomLeft, topRight);
                indices.push(topRight, bottomLeft, bottomRight);
            }
        }

        return {
            textureData: new Float32Array(textureCoordinates),
            indexData: new Uint16Array(indices)
        };
    }

    function setupWebGLCloth(surface) {
        var columns = 48;
        var rows = 12;
        var rowLength = columns + 1;
        var particleCount = rowLength * (rows + 1);
        var fixedStep = 1 / 60;
        var ambientMotionSpeed = getTuningNumber('cloth-speed', defaultAmbientMotionSpeed, .35, 3);
        var hoverMotionSpeed = getTuningNumber('cloth-hover-speed', defaultHoverMotionSpeed, .5, 4);
        var ambientMotionStrength = getTuningNumber('cloth-idle-strength', defaultAmbientMotionStrength, .5, 5);
        var currentAmbientMotionSpeed = ambientMotionSpeed;
        var image;
        var canvas;
        var gl = null;
        var program = null;
        var texture = null;
        var positionBuffer = null;
        var normalBuffer = null;
        var textureBuffer = null;
        var indexBuffer = null;
        var indexCount = 0;
        var locations = null;
        var positions = new Float32Array(particleCount * 3);
        var previousPositions = new Float32Array(particleCount * 3);
        var restPositions = new Float32Array(particleCount * 3);
        var normals = new Float32Array(particleCount * 3);
        var constraints = [];
        var clothAspect = 1;
        var renderScaleX = 1;
        var renderScaleY = 1;
        var resizeObserver;
        var intersectionObserver;
        var frame = null;
        var lastFrameSeconds = null;
        var accumulator = 0;
        var clothTime = 0;
        var gustEnergy = 0;
        var lastClientX = null;
        var lastClientY = null;
        var lastPointerTime = null;
        var lastImpulseTime = -Infinity;
        var isPointerOver = false;
        var isVisible = true;
        var isExiting = false;
        var isDestroyed = false;
        var contextLost = false;
        var usesLegacyMediaQueryListeners = false;

        var vertexSource = [
            'precision highp float;',
            'attribute vec3 a_position;',
            'attribute vec3 a_normal;',
            'attribute vec2 a_texture_coordinate;',
            'uniform float u_aspect;',
            'uniform float u_idle_phase;',
            'uniform float u_idle_strength;',
            'uniform vec2 u_render_scale;',
            'varying vec2 v_texture_coordinate;',
            'varying float v_fabric_light;',
            'void main() {',
            '    float idle_freedom = smoothstep(0.0, 1.0, a_texture_coordinate.x);',
            '    float idle_primary = sin((a_texture_coordinate.x * 6.15) - (u_idle_phase * 0.38) + (a_texture_coordinate.y * 0.82));',
            '    float idle_secondary = sin((a_texture_coordinate.x * 10.4) - (u_idle_phase * 0.21) - (a_texture_coordinate.y * 1.15)) * 0.24;',
            '    float idle_wave = (idle_primary + idle_secondary) * idle_freedom * u_idle_strength;',
            '    vec3 rendered_position = a_position;',
            '    rendered_position.x += idle_wave * u_aspect * 0.0032;',
            '    rendered_position.y += idle_wave * 0.014;',
            '    rendered_position.z += idle_wave * 0.02;',
            '    float depth = clamp(rendered_position.z, -0.2, 0.2);',
            '    float perspective = 1.0 + (depth * 0.055);',
            '    vec2 projected = vec2(((rendered_position.x / u_aspect) * 2.0) - 1.0, 1.0 - (rendered_position.y * 2.0));',
            '    projected *= perspective;',
            '    projected.y += depth * 0.035;',
            '    projected *= u_render_scale * vec2(0.992, 0.96);',
            '    vec3 lightDirection = normalize(vec3(-0.34, -0.24, 1.0));',
            '    float diffuse = dot(normalize(a_normal), lightDirection);',
            '    v_fabric_light = clamp(0.93 + (diffuse * 0.075), 0.9, 1.025);',
            '    v_texture_coordinate = a_texture_coordinate;',
            '    gl_Position = vec4(projected, depth * -0.08, 1.0);',
            '}'
        ].join('\n');

        var fragmentSource = [
            'precision mediump float;',
            'uniform sampler2D u_texture;',
            'varying vec2 v_texture_coordinate;',
            'varying float v_fabric_light;',
            'void main() {',
            '    vec4 colour = texture2D(u_texture, v_texture_coordinate);',
            '    gl_FragColor = vec4(colour.rgb * v_fabric_light, colour.a);',
            '}'
        ].join('\n');

        if (!surface
            || surface.getAttribute('data-music-library-logo-effect') !== 'cloth'
            || surface.getAttribute('data-music-library-logo-cloth-setup') === 'true') {
            return;
        }

        image = surface.querySelector('[data-music-library-logo-image]');
        canvas = surface.querySelector('[data-music-library-logo-canvas]');

        if (!image || !canvas || typeof canvas.getContext !== 'function') {
            return;
        }

        surface.setAttribute('data-music-library-logo-cloth-setup', 'true');
        surface.setAttribute('data-music-library-logo-idle-speed', ambientMotionSpeed.toFixed(2));
        surface.setAttribute('data-music-library-logo-hover-speed', hoverMotionSpeed.toFixed(2));
        surface.setAttribute('data-music-library-logo-idle-strength', ambientMotionStrength.toFixed(2));

        function particleIndex(x, y) {
            return (y * rowLength) + x;
        }

        function distanceBetween(first, second) {
            var firstOffset = first * 3;
            var secondOffset = second * 3;
            var deltaX = restPositions[secondOffset] - restPositions[firstOffset];
            var deltaY = restPositions[secondOffset + 1] - restPositions[firstOffset + 1];
            var deltaZ = restPositions[secondOffset + 2] - restPositions[firstOffset + 2];

            return Math.sqrt((deltaX * deltaX) + (deltaY * deltaY) + (deltaZ * deltaZ));
        }

        function addConstraint(first, second, stiffness) {
            constraints.push({
                first: first,
                second: second,
                length: distanceBetween(first, second),
                stiffness: stiffness
            });
        }

        function initialiseCloth() {
            var x;
            var y;

            clothAspect = image.naturalWidth / image.naturalHeight;
            constraints = [];

            for (y = 0; y <= rows; y += 1) {
                for (x = 0; x <= columns; x += 1) {
                    var index = particleIndex(x, y);
                    var offset = index * 3;
                    var positionX = (x / columns) * clothAspect;
                    var positionY = y / rows;

                    positions[offset] = positionX;
                    positions[offset + 1] = positionY;
                    positions[offset + 2] = 0;
                    previousPositions[offset] = positionX;
                    previousPositions[offset + 1] = positionY;
                    previousPositions[offset + 2] = 0;
                    restPositions[offset] = positionX;
                    restPositions[offset + 1] = positionY;
                    restPositions[offset + 2] = 0;
                    normals[offset] = 0;
                    normals[offset + 1] = 0;
                    normals[offset + 2] = 1;
                }
            }

            for (y = 0; y <= rows; y += 1) {
                for (x = 0; x <= columns; x += 1) {
                    var current = particleIndex(x, y);

                    if (x < columns) {
                        addConstraint(current, particleIndex(x + 1, y), .96);
                    }

                    if (y < rows) {
                        addConstraint(current, particleIndex(x, y + 1), .96);
                    }

                    if (x < columns && y < rows) {
                        addConstraint(current, particleIndex(x + 1, y + 1), .72);
                        addConstraint(particleIndex(x + 1, y), particleIndex(x, y + 1), .72);
                    }

                    if (x < columns - 1) {
                        addConstraint(current, particleIndex(x + 2, y), .38);
                    }

                    if (y < rows - 1) {
                        addConstraint(current, particleIndex(x, y + 2), .38);
                    }
                }
            }

            clothTime = 0;
            accumulator = 0;
            lastFrameSeconds = null;
            gustEnergy = 0;
        }

        function clearWebGLReferences() {
            program = null;
            texture = null;
            positionBuffer = null;
            normalBuffer = null;
            textureBuffer = null;
            indexBuffer = null;
            indexCount = 0;
            locations = null;
            gl = null;
        }

        function releaseWebGLResources() {
            if (gl && (!gl.isContextLost || !gl.isContextLost())) {
                if (positionBuffer) {
                    gl.deleteBuffer(positionBuffer);
                }

                if (normalBuffer) {
                    gl.deleteBuffer(normalBuffer);
                }

                if (textureBuffer) {
                    gl.deleteBuffer(textureBuffer);
                }

                if (indexBuffer) {
                    gl.deleteBuffer(indexBuffer);
                }

                if (texture) {
                    gl.deleteTexture(texture);
                }

                if (program) {
                    gl.deleteProgram(program);
                }
            }

            clearWebGLReferences();
        }

        function initialiseWebGL() {
            var mesh;

            if (isDestroyed || contextLost || !surface.isConnected) {
                return false;
            }

            if (gl && program) {
                return true;
            }

            gl = canvas.getContext('webgl', {
                alpha: true,
                antialias: true,
                premultipliedAlpha: true,
                preserveDrawingBuffer: false
            }) || canvas.getContext('experimental-webgl', {
                alpha: true,
                antialias: true,
                premultipliedAlpha: true,
                preserveDrawingBuffer: false
            });

            if (!gl) {
                surface.setAttribute('data-music-library-logo-cloth-unavailable', 'true');
                return false;
            }

            program = createProgram(gl, vertexSource, fragmentSource);

            if (!program) {
                surface.setAttribute('data-music-library-logo-cloth-unavailable', 'true');
                gl = null;
                return false;
            }

            mesh = createMesh(columns, rows);
            initialiseCloth();

            positionBuffer = gl.createBuffer();
            gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
            gl.bufferData(gl.ARRAY_BUFFER, positions, gl.DYNAMIC_DRAW);

            normalBuffer = gl.createBuffer();
            gl.bindBuffer(gl.ARRAY_BUFFER, normalBuffer);
            gl.bufferData(gl.ARRAY_BUFFER, normals, gl.DYNAMIC_DRAW);

            textureBuffer = gl.createBuffer();
            gl.bindBuffer(gl.ARRAY_BUFFER, textureBuffer);
            gl.bufferData(gl.ARRAY_BUFFER, mesh.textureData, gl.STATIC_DRAW);

            indexBuffer = gl.createBuffer();
            gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer);
            gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, mesh.indexData, gl.STATIC_DRAW);
            indexCount = mesh.indexData.length;

            texture = gl.createTexture();
            gl.bindTexture(gl.TEXTURE_2D, texture);
            gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);

            locations = {
                position: gl.getAttribLocation(program, 'a_position'),
                normal: gl.getAttribLocation(program, 'a_normal'),
                textureCoordinate: gl.getAttribLocation(program, 'a_texture_coordinate'),
                aspect: gl.getUniformLocation(program, 'u_aspect'),
                idlePhase: gl.getUniformLocation(program, 'u_idle_phase'),
                idleStrength: gl.getUniformLocation(program, 'u_idle_strength'),
                renderScale: gl.getUniformLocation(program, 'u_render_scale'),
                texture: gl.getUniformLocation(program, 'u_texture')
            };

            gl.useProgram(program);
            gl.enableVertexAttribArray(locations.position);
            gl.enableVertexAttribArray(locations.normal);
            gl.enableVertexAttribArray(locations.textureCoordinate);
            gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer);
            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, texture);
            gl.uniform1i(locations.texture, 0);
            gl.uniform1f(locations.aspect, clothAspect);
            gl.uniform1f(locations.idlePhase, clothTime);
            gl.uniform1f(locations.idleStrength, ambientMotionStrength);
            gl.uniform2f(locations.renderScale, renderScaleX, renderScaleY);
            gl.enable(gl.BLEND);
            gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
            gl.clearColor(0, 0, 0, 0);
            surface.removeAttribute('data-music-library-logo-cloth-unavailable');
            surface.setAttribute('data-music-library-logo-cloth-ready', 'true');

            return true;
        }

        function pinParticle(index) {
            var offset = index * 3;

            positions[offset] = restPositions[offset];
            positions[offset + 1] = restPositions[offset + 1];
            positions[offset + 2] = restPositions[offset + 2];
        }

        function integrateParticles(time) {
            var damping = .986;
            var ambientTime = time;
            var x;
            var y;

            for (y = 0; y <= rows; y += 1) {
                for (x = 0; x <= columns; x += 1) {
                    var index = particleIndex(x, y);
                    var offset = index * 3;

                    if (x === 0) {
                        pinParticle(index);
                        previousPositions[offset] = restPositions[offset];
                        previousPositions[offset + 1] = restPositions[offset + 1];
                        previousPositions[offset + 2] = restPositions[offset + 2];
                        continue;
                    }

                    var positionX = positions[offset];
                    var positionY = positions[offset + 1];
                    var positionZ = positions[offset + 2];
                    var velocityX = (positionX - previousPositions[offset]) * damping;
                    var velocityY = (positionY - previousPositions[offset + 1]) * damping;
                    var velocityZ = (positionZ - previousPositions[offset + 2]) * damping;
                    var u = x / columns;
                    var v = y / rows;
                    var freedom = Math.pow(u, .68);
                    var breathing = .82 + (Math.sin((ambientTime * .12) + (Math.sin(ambientTime * .049) * .7)) * .18);
                    var primaryWind = Math.sin((u * 6.15) - (ambientTime * .38) + (v * .82));
                    var secondaryWind = Math.sin((u * 10.4) - (ambientTime * .21) - (v * 1.15)) * .24;
                    var wind = (primaryWind + secondaryWind) * breathing;
                    var returnStrength = .00042 + ((1 - gustEnergy) * .00028);

                    previousPositions[offset] = positionX;
                    previousPositions[offset + 1] = positionY;
                    previousPositions[offset + 2] = positionZ;
                    positions[offset] = positionX + velocityX + (wind * freedom * .000002 * ambientMotionStrength) + ((restPositions[offset] - positionX) * returnStrength * .42);
                    positions[offset + 1] = positionY + velocityY + (wind * freedom * .0000042 * ambientMotionStrength) + ((restPositions[offset + 1] - positionY) * returnStrength);
                    positions[offset + 2] = clamp(positionZ + velocityZ + (wind * freedom * ((.00003 * ambientMotionStrength) + (gustEnergy * .000005))) + ((restPositions[offset + 2] - positionZ) * returnStrength * 1.35), -.16, .16);
                }
            }
        }

        function satisfyConstraint(constraint) {
            var firstOffset = constraint.first * 3;
            var secondOffset = constraint.second * 3;
            var deltaX = positions[secondOffset] - positions[firstOffset];
            var deltaY = positions[secondOffset + 1] - positions[firstOffset + 1];
            var deltaZ = positions[secondOffset + 2] - positions[firstOffset + 2];
            var distance = Math.sqrt((deltaX * deltaX) + (deltaY * deltaY) + (deltaZ * deltaZ));
            var firstPinned = constraint.first % rowLength === 0;
            var secondPinned = constraint.second % rowLength === 0;
            var correction;
            var firstShare;
            var secondShare;

            if (distance < .000001 || (firstPinned && secondPinned)) {
                return;
            }

            correction = ((distance - constraint.length) / distance) * constraint.stiffness;
            firstShare = firstPinned ? 0 : (secondPinned ? 1 : .5);
            secondShare = secondPinned ? 0 : (firstPinned ? 1 : .5);

            positions[firstOffset] += deltaX * correction * firstShare;
            positions[firstOffset + 1] += deltaY * correction * firstShare;
            positions[firstOffset + 2] += deltaZ * correction * firstShare;
            positions[secondOffset] -= deltaX * correction * secondShare;
            positions[secondOffset + 1] -= deltaY * correction * secondShare;
            positions[secondOffset + 2] -= deltaZ * correction * secondShare;
        }

        function solveConstraints() {
            var iteration;
            var index;
            var y;

            for (iteration = 0; iteration < 4; iteration += 1) {
                for (index = 0; index < constraints.length; index += 1) {
                    satisfyConstraint(constraints[index]);
                }

                for (y = 0; y <= rows; y += 1) {
                    pinParticle(particleIndex(0, y));
                }
            }
        }

        function calculateNormals() {
            var x;
            var y;

            for (y = 0; y <= rows; y += 1) {
                for (x = 0; x <= columns; x += 1) {
                    var index = particleIndex(x, y);
                    var offset = index * 3;
                    var left = particleIndex(Math.max(0, x - 1), y) * 3;
                    var right = particleIndex(Math.min(columns, x + 1), y) * 3;
                    var top = particleIndex(x, Math.max(0, y - 1)) * 3;
                    var bottom = particleIndex(x, Math.min(rows, y + 1)) * 3;
                    var tangentXx = positions[right] - positions[left];
                    var tangentXy = positions[right + 1] - positions[left + 1];
                    var tangentXz = positions[right + 2] - positions[left + 2];
                    var tangentYx = positions[bottom] - positions[top];
                    var tangentYy = positions[bottom + 1] - positions[top + 1];
                    var tangentYz = positions[bottom + 2] - positions[top + 2];
                    var normalX = (tangentXy * tangentYz) - (tangentXz * tangentYy);
                    var normalY = (tangentXz * tangentYx) - (tangentXx * tangentYz);
                    var normalZ = (tangentXx * tangentYy) - (tangentXy * tangentYx);
                    var normalLength = Math.sqrt((normalX * normalX) + (normalY * normalY) + (normalZ * normalZ)) || 1;

                    normals[offset] = normalX / normalLength;
                    normals[offset + 1] = normalY / normalLength;
                    normals[offset + 2] = normalZ / normalLength;
                }
            }
        }

        function stepPhysics() {
            var targetMotionSpeed = isPointerOver ? hoverMotionSpeed : ambientMotionSpeed;
            var speedEase = isPointerOver ? .055 : .025;

            currentAmbientMotionSpeed += (targetMotionSpeed - currentAmbientMotionSpeed) * speedEase;
            clothTime += fixedStep * currentAmbientMotionSpeed;
            integrateParticles(clothTime);
            solveConstraints();
            gustEnergy *= .982;
        }

        function uploadCloth() {
            gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
            gl.bufferSubData(gl.ARRAY_BUFFER, 0, positions);
            gl.vertexAttribPointer(locations.position, 3, gl.FLOAT, false, 0, 0);

            gl.bindBuffer(gl.ARRAY_BUFFER, normalBuffer);
            gl.bufferSubData(gl.ARRAY_BUFFER, 0, normals);
            gl.vertexAttribPointer(locations.normal, 3, gl.FLOAT, false, 0, 0);

            gl.bindBuffer(gl.ARRAY_BUFFER, textureBuffer);
            gl.vertexAttribPointer(locations.textureCoordinate, 2, gl.FLOAT, false, 0, 0);
        }

        function surfaceIntersectsViewport() {
            var bounds = surface.getBoundingClientRect();

            return bounds.width > 0
                && bounds.height > 0
                && bounds.bottom > 0
                && bounds.right > 0
                && bounds.top < window.innerHeight
                && bounds.left < window.innerWidth;
        }

        function prepareCanvasForTakeover() {
            surface.classList.remove('is-logo-distortion-ready');
            image.style.transition = 'none';
            image.style.opacity = '1';
            canvas.style.display = 'block';
            canvas.style.opacity = '0';
        }

        function takeCanvasOwnership() {
            // Keep the ownership handoff atomic: the PNG is hidden in the same
            // rendering step in which the first valid WebGL frame is exposed.
            image.style.transition = 'none';
            image.style.removeProperty('opacity');
            surface.classList.add('is-logo-distortion-ready');
            canvas.style.removeProperty('display');
            canvas.style.removeProperty('opacity');
        }

        function showStaticFallback() {
            surface.classList.remove('is-logo-distortion-ready');
            image.style.transition = 'none';
            image.style.opacity = '1';
            canvas.style.display = 'none';
            canvas.style.opacity = '0';
        }

        function renderFrame(milliseconds) {
            var seconds = milliseconds * .001;
            var elapsed;
            var steps = 0;

            frame = null;

            if (isDestroyed || !surface.isConnected) {
                destroy();
                return;
            }

            if (!isVisible && surfaceIntersectsViewport()) {
                isVisible = true;
            }

            if (isExiting || document.hidden || !isVisible) {
                lastFrameSeconds = null;
                return;
            }

            if (contextLost || !effectIsAvailable() || !gl || !program) {
                showStaticFallback();
                lastFrameSeconds = null;
                return;
            }

            if (lastFrameSeconds === null) {
                lastFrameSeconds = seconds;
            }

            elapsed = Math.min(.05, Math.max(0, seconds - lastFrameSeconds));
            lastFrameSeconds = seconds;
            accumulator += elapsed;

            while (accumulator >= fixedStep && steps < 3) {
                stepPhysics();
                accumulator -= fixedStep;
                steps += 1;
            }

            calculateNormals();
            gl.useProgram(program);
            gl.clear(gl.COLOR_BUFFER_BIT);
            gl.uniform1f(locations.aspect, clothAspect);
            gl.uniform1f(locations.idlePhase, clothTime);
            gl.uniform1f(locations.idleStrength, ambientMotionStrength);
            gl.uniform2f(locations.renderScale, renderScaleX, renderScaleY);
            uploadCloth();
            gl.drawElements(gl.TRIANGLES, indexCount, gl.UNSIGNED_SHORT, 0);

            if (!surface.classList.contains('is-logo-distortion-ready')
                && (!gl.isContextLost || !gl.isContextLost())
                && gl.getError() === gl.NO_ERROR) {
                takeCanvasOwnership();
            }

            surface.setAttribute('data-music-library-logo-wind', gustEnergy.toFixed(3));
            surface.setAttribute('data-music-library-logo-speed', currentAmbientMotionSpeed.toFixed(2));
            scheduleFrame();
        }

        function scheduleFrame() {
            if (!isVisible && surface.isConnected && surfaceIntersectsViewport()) {
                isVisible = true;
            }

            if (!isDestroyed && surface.isConnected && !isExiting && !contextLost && frame === null && !document.hidden && isVisible) {
                frame = window.requestAnimationFrame(renderFrame);
            }
        }

        function resizeCanvas() {
            var canvasBounds;
            var imageBounds;
            var pixelRatio;
            var width;
            var height;

            if (isDestroyed || !surface.isConnected || isExiting) {
                return;
            }

            if (contextLost || !image.complete || image.naturalWidth === 0 || !effectIsAvailable()) {
                showStaticFallback();
                return;
            }

            if (!initialiseWebGL()) {
                showStaticFallback();
                return;
            }

            if (!surface.classList.contains('is-logo-distortion-ready')) {
                prepareCanvasForTakeover();
            }

            canvasBounds = canvas.getBoundingClientRect();
            imageBounds = image.getBoundingClientRect();
            pixelRatio = Math.min(2, window.devicePixelRatio || 1);
            width = Math.max(1, Math.round(canvasBounds.width * pixelRatio));
            height = Math.max(1, Math.round(canvasBounds.height * pixelRatio));
            renderScaleX = clamp(imageBounds.width / canvasBounds.width, .5, 1);
            renderScaleY = clamp(imageBounds.height / canvasBounds.height, .5, 1);

            if (canvas.width !== width || canvas.height !== height) {
                canvas.width = width;
                canvas.height = height;
                gl.viewport(0, 0, width, height);
            }

            scheduleFrame();
        }

        function pauseBeforeExit() {
            isExiting = true;

            if (frame !== null) {
                window.cancelAnimationFrame(frame);
                frame = null;
            }

            lastFrameSeconds = null;
        }

        function restoreAfterPageShow() {
            if (isDestroyed || !surface.isConnected) {
                return;
            }

            isExiting = false;
            isVisible = true;
            lastFrameSeconds = null;
            resizeCanvas();
        }

        function applyPointerImpulse(normalizedX, normalizedY, directionX, directionY, energy) {
            var depthDirection = Math.abs(directionX) > .08 ? (directionX < 0 ? -1 : 1) : 1;
            var x;
            var y;

            for (y = 0; y <= rows; y += 1) {
                for (x = 1; x <= columns; x += 1) {
                    var u = x / columns;
                    var v = y / rows;
                    var deltaX = (u - normalizedX) * 1.28;
                    var deltaY = (v - normalizedY) * .82;
                    var influence = Math.exp(-((deltaX * deltaX) + (deltaY * deltaY)) * 11.5);
                    var freedom = .16 + (.84 * Math.pow(u, .62));
                    var impulse = influence * freedom * energy;
                    var offset = particleIndex(x, y) * 3;

                    previousPositions[offset] -= directionX * impulse * .002;
                    previousPositions[offset + 1] -= ((directionY * .0055) + (directionX * .0012)) * impulse;
                    previousPositions[offset + 2] -= depthDirection * impulse * (.006 + (energy * .009));
                }
            }

            gustEnergy = Math.max(gustEnergy, energy);
            scheduleFrame();
        }

        function addWindImpulse(event) {
            var bounds;
            var now;
            var elapsed;
            var movementX;
            var movementY;
            var distance;
            var speed;
            var energy;
            var length;

            if (!effectIsAvailable() || event.pointerType === 'touch') {
                return;
            }

            bounds = image.getBoundingClientRect();

            if (!bounds.width || !bounds.height) {
                return;
            }

            now = window.performance.now();

            if (lastClientX === null || lastClientY === null || lastPointerTime === null) {
                lastClientX = event.clientX;
                lastClientY = event.clientY;
                lastPointerTime = now;
                return;
            }

            elapsed = Math.max(8, now - lastPointerTime);
            movementX = event.clientX - lastClientX;
            movementY = event.clientY - lastClientY;
            distance = Math.sqrt((movementX * movementX) + (movementY * movementY));
            speed = distance / elapsed;
            energy = clamp((speed - .02) / .9, .015, 1);
            length = Math.max(1, distance);

            if (distance > .7 && now - lastImpulseTime > 42) {
                applyPointerImpulse(
                    clamp((event.clientX - bounds.left) / bounds.width, 0, 1),
                    clamp((event.clientY - bounds.top) / bounds.height, 0, 1),
                    movementX / length,
                    movementY / length,
                    energy
                );
                lastImpulseTime = now;
            }

            lastClientX = event.clientX;
            lastClientY = event.clientY;
            lastPointerTime = now;
        }

        function addEntryImpulse(event) {
            var bounds;
            var normalizedX;
            var normalizedY;
            var directionX;
            var directionY;

            if (!effectIsAvailable() || event.pointerType === 'touch') {
                return;
            }

            bounds = image.getBoundingClientRect();

            if (!bounds.width || !bounds.height) {
                return;
            }

            normalizedX = clamp((event.clientX - bounds.left) / bounds.width, 0, 1);
            normalizedY = clamp((event.clientY - bounds.top) / bounds.height, 0, 1);
            directionX = normalizedX < .5 ? .45 : -.45;
            directionY = normalizedY < .5 ? .2 : -.2;
            applyPointerImpulse(normalizedX, normalizedY, directionX, directionY, .055);
        }

        function handlePointerEnter(event) {
            if (!effectIsAvailable() || event.pointerType === 'touch') {
                return;
            }

            lastClientX = event.clientX;
            lastClientY = event.clientY;
            lastPointerTime = window.performance.now();
            isPointerOver = true;
            addEntryImpulse(event);
        }

        function handlePointerLeave() {
            isPointerOver = false;
            lastClientX = null;
            lastClientY = null;
            lastPointerTime = null;
        }

        function handleWebGLContextLost(event) {
            event.preventDefault();
            contextLost = true;

            if (frame !== null) {
                window.cancelAnimationFrame(frame);
                frame = null;
            }

            surface.classList.remove('is-logo-distortion-ready');
            surface.removeAttribute('data-music-library-logo-cloth-ready');
            surface.setAttribute('data-music-library-logo-cloth-unavailable', 'true');
            showStaticFallback();
            releaseWebGLResources();
        }

        function handleWebGLContextRestored() {
            if (isDestroyed || !surface.isConnected) {
                return;
            }

            contextLost = false;
            surface.removeAttribute('data-music-library-logo-cloth-unavailable');
            prepareCanvasForTakeover();
            lastFrameSeconds = null;
            accumulator = 0;
            resizeCanvas();
        }

        surface.addEventListener('pointerenter', handlePointerEnter);
        surface.addEventListener('pointermove', addWindImpulse);
        surface.addEventListener('pointerleave', handlePointerLeave);
        canvas.addEventListener('webglcontextlost', handleWebGLContextLost);
        canvas.addEventListener('webglcontextrestored', handleWebGLContextRestored);

        function refreshMode() {
            if (isDestroyed || !surface.isConnected) {
                return;
            }

            if (effectIsAvailable()) {
                prepareCanvasForTakeover();
                resizeCanvas();
                return;
            }

            if (frame !== null) {
                window.cancelAnimationFrame(frame);
                frame = null;
            }

            lastClientX = null;
            lastClientY = null;
            lastPointerTime = null;
            isPointerOver = false;
            showStaticFallback();
        }

        function refreshVisibility() {
            if (isDestroyed || !surface.isConnected) {
                return;
            }

            if (document.hidden) {
                if (frame !== null) {
                    window.cancelAnimationFrame(frame);
                    frame = null;
                }

                lastFrameSeconds = null;
                return;
            }

            scheduleFrame();
        }

        function destroy() {
            var runtimeAttributes = [
                'data-music-library-logo-cloth-setup',
                'data-music-library-logo-cloth-ready',
                'data-music-library-logo-cloth-unavailable',
                'data-music-library-logo-idle-speed',
                'data-music-library-logo-hover-speed',
                'data-music-library-logo-idle-strength',
                'data-music-library-logo-wind',
                'data-music-library-logo-speed'
            ];

            if (isDestroyed) {
                return;
            }

            isDestroyed = true;
            isExiting = true;
            contextLost = false;

            if (frame !== null) {
                window.cancelAnimationFrame(frame);
                frame = null;
            }

            if (resizeObserver) {
                resizeObserver.disconnect();
            }

            if (intersectionObserver) {
                intersectionObserver.disconnect();
            }

            if (typeof finePointerQuery.removeEventListener === 'function') {
                finePointerQuery.removeEventListener('change', refreshMode);
                reducedMotionQuery.removeEventListener('change', refreshMode);
            } else if (usesLegacyMediaQueryListeners && typeof finePointerQuery.removeListener === 'function') {
                finePointerQuery.removeListener(refreshMode);
                reducedMotionQuery.removeListener(refreshMode);
            }

            window.removeEventListener('resize', resizeCanvas);
            document.removeEventListener('visibilitychange', refreshVisibility);
            surface.removeEventListener('pointerenter', handlePointerEnter);
            surface.removeEventListener('pointermove', addWindImpulse);
            surface.removeEventListener('pointerleave', handlePointerLeave);
            surface.removeEventListener(exitEventName, pauseBeforeExit);
            surface.removeEventListener(restoreEventName, restoreAfterPageShow);
            surface.removeEventListener(destroyEventName, destroy);
            canvas.removeEventListener('webglcontextlost', handleWebGLContextLost);
            canvas.removeEventListener('webglcontextrestored', handleWebGLContextRestored);
            image.removeEventListener('load', resizeCanvas);

            releaseWebGLResources();
            surface.classList.remove('is-logo-distortion-ready');
            image.style.removeProperty('transition');
            image.style.removeProperty('opacity');
            canvas.style.removeProperty('display');
            canvas.style.removeProperty('opacity');
            runtimeAttributes.forEach(function (attribute) {
                surface.removeAttribute(attribute);
            });
        }

        if (typeof finePointerQuery.addEventListener === 'function') {
            finePointerQuery.addEventListener('change', refreshMode);
            reducedMotionQuery.addEventListener('change', refreshMode);
        } else if (typeof finePointerQuery.addListener === 'function') {
            usesLegacyMediaQueryListeners = true;
            finePointerQuery.addListener(refreshMode);
            reducedMotionQuery.addListener(refreshMode);
        }

        if (typeof window.ResizeObserver === 'function') {
            resizeObserver = new window.ResizeObserver(resizeCanvas);
            resizeObserver.observe(image);
        } else {
            window.addEventListener('resize', resizeCanvas);
        }

        if (typeof window.IntersectionObserver === 'function') {
            intersectionObserver = new window.IntersectionObserver(function (entries) {
                isVisible = entries[0].isIntersecting
                    || (!document.hidden && surfaceIntersectsViewport());

                if (isVisible && effectIsAvailable() && gl && program) {
                    lastFrameSeconds = null;
                    scheduleFrame();
                } else {
                    if (frame !== null) {
                        window.cancelAnimationFrame(frame);
                        frame = null;
                    }
                }
            }, { threshold: .01 });
            intersectionObserver.observe(surface);
        }

        document.addEventListener('visibilitychange', refreshVisibility);
        surface.addEventListener(exitEventName, pauseBeforeExit);
        surface.addEventListener(restoreEventName, restoreAfterPageShow);
        surface.addEventListener(destroyEventName, destroy);

        if (image.complete) {
            resizeCanvas();
        } else {
            image.addEventListener('load', resizeCanvas, { once: true });
        }
    }

    function setupWebGLCloths(root) {
        var logos = [];

        collectClothSurfaces(root, logos);
        Array.prototype.forEach.call(logos, setupWebGLCloth);
    }

    function boot() {
        setupWebGLCloths(document);
        observeClothLifecycle();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot, { once: true });
    } else {
        boot();
    }

    document.addEventListener('pointerover', function (event) {
        var surface = event.target.closest('[data-music-library-logo-effect="cloth"]');

        if (surface) {
            setupWebGLCloth(surface);
        }
    });

    window.addEventListener('pageswap', function () {
        dispatchLifecycleEvent(exitEventName);
    });
    window.addEventListener('pagehide', function () {
        dispatchLifecycleEvent(exitEventName);
    });
    window.addEventListener('pageshow', restorePersistedPage);
})(window, document);
